/// <amd-module name="Widgets/QueryScalarWidget/QueryScalarWidget" />
import { ICacheableQueryService } from "Dashboards/WidgetCommon/DashboardsCacheableQueryService";
import { ITrackName } from "Dashboards/WidgetCommon/ITrackName";
import * as WidgetTelemetry from "Dashboards/WidgetCommon/WidgetTelemetry";
import { LoadingStatus, WidgetContextMenuColorMode, WidgetProps } from "Dashboards/WidgetContracts/WidgetProps";
import React = require("react");
import { Scalar } from "Reporting/Visuals/Scalar";
import { UnconfiguredMessage } from "Reporting/Visuals/VisualMessages/UnconfiguredMessage";
import * as TfsWidgetContext from "Tfs/Widget/Context";
import { ColorUtilities } from "VSS/Features/Charts/Internal/ColorUtilities";
import { VssComponent } from "VSS/Platform/Layout";
import * as VssLocation from "VSS/Platform/Location";
import * as VssUserClaims from "VSS/Platform/UserClaims";
import { isFeatureEnabled } from "VSS/Platform/Feature";
import { QueryScalarCacheableQuery } from "Widgets/QueryScalarWidget/QueryScalarCacheableQuery";
import * as Resources from "Widgets/QueryScalarWidget/Resources";
import { ColorRuleInterpreter, IQueryColorRule } from "./ColorRuleInterpreter";

/**
 * Describes the settings/configuration used by Query Scalar Widget and its configuration view
 * Foreground color is not user selectable, but a secret managed by client implementation, which is why it is not stored.
 */
export interface IQueryScalarConfiguration extends ITrackName {
    defaultBackgroundColor: string;

    // Rules must be evaluated in descending order.  The last rule to match is applied.
    colorRules: IQueryColorRule[];

    // ID for the selected query
    queryId: string;
}

interface IQueryScalarWidgetState {
    // -1 means that we didn't load the count as yet
    count: number;
}

export class QueryScalarWidget extends VssComponent<WidgetProps, IQueryScalarWidgetState> {
    constructor(props: WidgetProps) {
        super(props);
        this.state = { count: -1 };
    }

    public async componentDidMount() {
        this.fetchAndSetQueryCount();
        WidgetTelemetry.onWidgetLoaded(this.context.pageContext, this.props);
    }

    public async componentDidUpdate(prevProps: WidgetProps) {
        if (prevProps.settings != this.props.settings) {
            this.fetchAndSetQueryCount();
        }
    }

    public render(): JSX.Element {
        const configuration = this.parseSettings();

        if (!configuration) {
            return <UnconfiguredMessage visualTitle={this.props.name} size={this.props.size} onClick={this.props.onConfigure} />;
        }

        return (
            <Scalar
                backgroundColor={this.getBackgroundColor(configuration)}
                subtitle={Resources.Subtitle}
                title={this.props.name}
                value={this.state.count}
                targetUrl={this.getQueryResultsUrl(configuration.queryId)}
                disableFPS={true}
                onClick={() => WidgetTelemetry.onWidgetClick(this.context.pageContext, this.props)}
            />
        );
    }

    private async fetchAndSetQueryCount() {
        const configuration = this.parseSettings();

        if (!configuration) {
            this.props.onWidgetLoaded();
            return;
        }

        const cacheableQueryService = this.context.pageContext.getService<ICacheableQueryService>("IDashboardsCacheableQueryService");
        const queryScalarQuery = new QueryScalarCacheableQuery(configuration.queryId, this.getProjectId(), this.props.teamContext.id);

        try {
            const count = await cacheableQueryService.getCacheableQueryResult(queryScalarQuery);
            this.setState({ count: count });
            this.props.onWidgetLoaded();
            this.setWidgetMenuColorMode(configuration);
        } catch (error) {
            this.props.onWidgetLoaded({ outcome: LoadingStatus.failure, outcomeDescription: error });
        }
    }

    private parseSettings(): IQueryScalarConfiguration | null {
        try {
            return this.props.settings !== null && JSON.parse(this.props.settings);
        } catch (e) {
            // Express non-parsable/invalid configurations as null setting
            return null;
        }
    }

    private getQueryResultsUrl(id: string): string | undefined {
        const isUserMember = VssUserClaims.userHasClaim(this.context.pageContext, VssUserClaims.UserClaims.Member);
        if (!isUserMember) {
            return undefined;
        }

        const queryResultsRouteId = this.getRouteId();
        const queryResultsViewId = "query";

        return VssLocation.routeUrl(this.context.pageContext, queryResultsRouteId, {
            project: this.getProjectId(),
            view: queryResultsViewId,
            id: id
        });
    }

    private getRouteId(): string{
        if (isFeatureEnabled(this.context.pageContext, "ms.vss-work-web.new-boards-hub-feature", false)){
            return "ms.vss-work-web.new-queries-hub-route";
        }

        return "ms.vss-work-web.query-route";
    }

    private getBackgroundColor(configuration: IQueryScalarConfiguration): string {
        if (!configuration) {
            throw Error("This shouldn't be called with an invalid configuration");
        }

        return ColorRuleInterpreter.getBackgroundColor(configuration.defaultBackgroundColor, configuration.colorRules, this.state.count);
    }

    private setWidgetMenuColorMode(configuration: IQueryScalarConfiguration): void {
        if (!this.props.settings) {
            throw Error("This shouldn't be called with an invalid configuration");
        }

        const backgroundColor = this.getBackgroundColor(configuration);
        const isDark = ColorUtilities.isDark(backgroundColor);
        const colorMode = isDark ? WidgetContextMenuColorMode.Dark : WidgetContextMenuColorMode.Default;
        this.props.setMenuColorMode(colorMode);
    }

    private getProjectId(): string {
        return TfsWidgetContext.getProject(this.context.pageContext).id;
    }
}

VssComponent.register("queryScalarWidget", QueryScalarWidget);
